/**
 * Axios Configuration with Credentials and CSRF Interceptor
 *
 * Configures axios for cookie-based authentication and CSRF protection.
 *
 * Key Features:
 * - Credentials: Enables sending cookies with all requests
 * - CSRF Protection: Automatically adds X-CSRF-Token header for mutating requests
 * - Error Handling: Intercepts 401/403 errors for authentication/CSRF failures
 *
 * Requirements:
 * - 1.8: CSRF token validation for POST/PUT/PATCH/DELETE requests
 * - 8.5: CORS with credentials enabled for cookie transmission
 */

import axios, { AxiosError } from "axios";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { getCsrfTokenForRequest } from "./csrf";

/**
 * Create and configure axios instance with credentials and CSRF interceptor.
 *
 * This instance is configured to work with the backend's cookie-based
 * authentication and CSRF protection middleware.
 *
 * @param baseURL - API base URL (from environment)
 * @returns Configured axios instance
 */
export function createAxiosInstance(baseURL: string): AxiosInstance {
  console.log('[AxiosConfig] Creating axios instance with baseURL:', baseURL);

  // Create axios instance with credentials enabled
  const instance = axios.create({
    baseURL,
    withCredentials: true, // Enable sending cookies with requests (Requirement 8.5)
    timeout: 30000, // 30 second timeout for API requests (increased for slow operations like task creation)
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor: Add CSRF token for mutating requests
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Get CSRF token for this request method
      const csrfToken = getCsrfTokenForRequest(config.method || "GET");

      console.log('[AxiosInterceptor] Request:', {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        fullURL: config.baseURL ? `${config.baseURL}${config.url}` : config.url,
        csrfToken: csrfToken ? `${csrfToken.substring(0, 10)}...` : null,
        hasAuthHeader: !!config.headers?.['Authorization']
      });

      // Add CSRF token header if required (Requirement 1.8)
      if (csrfToken && config.headers) {
        config.headers["X-CSRF-Token"] = csrfToken;
      }

      return config;
    },
    (error) => {
      // Request configuration error
      console.error("Request configuration error:", error);
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle authentication and CSRF errors
  instance.interceptors.response.use(
    (response) => {
      // Successful response - return as-is
      return response;
    },
    (error: AxiosError) => {
      // Check if error has a response
      if (error.response) {
        const status = error.response.status;
        const requestUrl = error.config?.url || "";

        // Handle 401 Unauthorized (session expired or invalid)
        if (status === 401) {
          // Don't redirect if:
          // 1. We're already on the login page
          // 2. This is a request to /auth/me (let authProvider.handle it)
          // 3. This is a request to /auth/login or /auth/callback (auth flow endpoints)
          const isLoginPage = window.location.pathname.includes("/login");
          const isAuthMeRequest = requestUrl.includes("/auth/me");
          const isAuthFlowRequest = requestUrl.includes("/auth/login") || requestUrl.includes("/auth/callback");

          if (!isLoginPage && !isAuthMeRequest && !isAuthFlowRequest) {
            console.warn("Authentication failed (401). Session may be expired. Redirecting to login...");
            // Only redirect for non-auth endpoints
            // The authProvider.onError will handle /auth/me requests
            window.location.href = "/login";
          } else {
            // For /auth/me requests, just log and let authProvider handle it
            console.warn("Authentication failed (401) for auth endpoint. Letting authProvider handle it.");
          }
        }

        // Handle 403 Forbidden (CSRF validation failed)
        else if (status === 403) {
          const errorData = error.response.data as any;

          // Check if it's a CSRF error
          if (errorData?.error === "csrf_validation_failed") {
            console.error("CSRF validation failed. Please refresh the page.");

            // You could show a toast notification here
            // For now, we'll let the dataProvider handle the display
          }
        }
      } else if (error.request) {
        // Request was made but no response received (network error, timeout)
        console.error("Network error:", error.message);
      } else {
        // Something else happened while setting up the request
        console.error("Request setup error:", error.message);
      }

      // Always reject to let calling code handle the error
      return Promise.reject(error);
    }
  );

  return instance;
}

/**
 * Default axios instance configured for the admin panel.
 *
 * This instance is created with the API base URL from environment.
 * In development, uses empty baseURL to leverage Vite's dev server proxy.
 * In production, uses the full API URL from environment.
 */
export const axiosInstance = createAxiosInstance(
  // Use empty baseURL in development to use Vite proxy
  // In production (built), VITE_API_URL will be the full API URL
  import.meta.env.MODE === "development" ? "" : (import.meta.env.VITE_API_URL || "http://localhost:8000")
);

/**
 * Export axios for cases where the base axios library is needed.
 * However, prefer using `axiosInstance` for all API calls.
 */
export { axios };
